interface SubdomainTenantCorrectionParams {
    /** Tenant ids associated with the subdomain the app is loaded on, from the subdomain lookup. */
    subdomainTenantIds: string[] | undefined;

    /** Tenant ids the signed-in user is a member of. */
    userTenants: string[] | undefined;

    /** The tenant currently resolved for the session (from preferredTenant), if any. */
    currentTenant: string | undefined;
}

interface SubdomainTenantCorrection {
    /** A single subdomain tenant to select, when the session has no preference of its own yet. */
    tenantToSwitchTo?: string;

    /**
     * The subdomain maps to several of the user's tenants and the session has no preference to honour,
     * so the user must be prompted to choose one.
     */
    requireSelection?: boolean;
}

/**
 * Decide whether to auto-select a tenant from the subdomain the app is loaded on.
 *
 * A stored preferredTenant is an explicit choice and always wins: when the session has already
 * resolved a tenant the user belongs to, this leaves it in place (the off-subdomain redirect in the
 * app-config gate then moves it to its own home if it doesn't belong to the current subdomain). The
 * subdomain only picks a tenant when there is no preference to honour yet, so that landing on a
 * subdomain with no chosen tenant still resolves to one without prompting where it can.
 *
 * The subdomain lookup gives the tenant ids behind the subdomain; we intersect those with the user's
 * own tenants so we never select a tenant they cannot access. The returned correction distinguishes:
 *  - `tenantToSwitchTo`: exactly one matching tenant, so select it.
 *  - `requireSelection`: several matching tenants, so the user must pick one.
 *  - empty object: nothing to do (not on a subdomain, no matching tenant, or a preference is set).
 */
export const findSubdomainTenantCorrection = ({
    subdomainTenantIds,
    userTenants,
    currentTenant
}: SubdomainTenantCorrectionParams): SubdomainTenantCorrection => {
    if (!subdomainTenantIds?.length || !userTenants?.length) {
        return {};
    }

    const hasPreferredTenant = currentTenant !== undefined && userTenants.includes(currentTenant);
    if (hasPreferredTenant) {
        return {};
    }

    const matchingTenants = subdomainTenantIds.filter((tenantId) => userTenants.includes(tenantId));

    if (matchingTenants.length === 0) {
        return {};
    }

    if (matchingTenants.length > 1) {
        return { requireSelection: true };
    }

    return { tenantToSwitchTo: matchingTenants[0] };
};
